import { useEffect, useMemo, useState } from 'react';
import type { GastoInterno, HoraInterna, ResumenInterno } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api } from '../api.js';
import { construirLibro, descargar, type Celda } from '../excel.js';
import { fechaLarga } from '../componentes.js';
import { Icono } from '../componentes/Iconos.js';
import { FacturaDelGasto, SenalFactura } from '../componentes/FacturaDelGasto.js';
import {
  AccionesCarga,
  ModalEditarGasto,
  ModalEditarHoras,
} from '../componentes/EditarCarga.js';

interface Props {
  idioma: Idioma;
  /**
   * Direccion ve la organizacion entera; un manager, solo lo suyo. Lo decide el
   * servidor —la consulta ya viene acotada—; esto es para no ENSENAR un filtro
   * por personas a quien solo va a encontrarse a si mismo.
   */
  esDireccion: boolean;
  onVolver: () => void;
}

type Pestana = 'gastos' | 'horas';

const CLASE_ESTADO: Record<string, string> = {
  PENDIENTE_APROBACION: 'ambar',
  APROBADO: 'info',
  RECHAZADO: 'rojo',
  PAGADO: 'verde',
};

/**
 * Interno: el coste de tener la empresa abierta, separado del que se imputa a
 * una villa. Gastos y horas del equipo sin propiedad, con su estado de
 * aprobación y de contabilización, tal y como se llevan en la operación real.
 *
 * LOS FILTROS SE APLICAN EN EL NAVEGADOR, no en el servidor, y es a propósito:
 * son unos cientos de líneas que ya están cargadas, así que filtrar aquí es
 * instantáneo y —lo que importa de verdad— garantiza que el Excel que se
 * descarga contiene EXACTAMENTE lo que se está viendo. Con el filtrado en el
 * servidor, la lista y el fichero pueden separarse sin que nadie lo note.
 */
export function Interno({ idioma, esDireccion, onVolver }: Props) {
  const [pestana, setPestana] = useState<Pestana>('gastos');
  const [resumen, setResumen] = useState<ResumenInterno | null>(null);
  const [gastos, setGastos] = useState<GastoInterno[] | null>(null);
  const [horas, setHoras] = useState<HoraInterna[] | null>(null);
  const [categoria, setCategoria] = useState('');
  const [estado, setEstado] = useState('');
  /*
    DE QUE SE ESTA HABLANDO. «Interno» nacio como «lo que no cuelga de ninguna
    casa», y eso dejaba las cargas de las casas sin ninguna pantalla desde la
    que mirarlas una a una: en Finanzas solo se ven ya sumadas y convertidas en
    lineas de factura, y para corregir un importe hay que poder ver la fila.
    Arranca en lo interno, que es lo que viene a ver quien entra aqui a diario.
  */
  const [ambito, setAmbito] = useState<'INTERNO' | 'PROPIEDADES' | 'TODO'>('INTERNO');
  const [error, setError] = useState<string | null>(null);

  // Filtros compartidos por las dos pestañas: quien busca «Bianca en agosto»
  // suele querer verlo en horas y en gastos sin volver a escribirlo.
  const [persona, setPersona] = useState('');
  /* El gasto cuya factura se esta mirando. */
  const [factura, setFactura] = useState<GastoInterno | null>(null);
  /*
    CORREGIR LO QUE SE CARGO MAL. Solo Direccion: el servidor lo exige y aqui ni
    siquiera se ensena a los demas, que es mejor que ensenar un boton que
    contesta «no puedes».
  */
  const [editandoGasto, setEditandoGasto] = useState<GastoInterno | null>(null);
  const [editandoHoras, setEditandoHoras] = useState<HoraInterna | null>(null);
  /* Sube al cambiar algo, y con el se vuelven a pedir las listas. */
  const [recarga, setRecarga] = useState(0);
  const [meses, setMeses] = useState<string[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [importeMin, setImporteMin] = useState('');
  const [importeMax, setImporteMax] = useState('');

  useEffect(() => {
    api
      .internoResumen()
      .then((r) => setResumen(r.resumen))
      .catch(() => setError(t(idioma, 'error.servidor')));
    api
      .internoHoras({ ambito })
      .then((r) => setHoras(r.horas))
      .catch(() => undefined);
  }, [idioma, recarga, ambito]);

  useEffect(() => {
    api
      .internoGastos({
        ...(categoria ? { categoria } : {}),
        ...(estado ? { estado } : {}),
        ambito,
      })
      .then((r) => setGastos(r.gastos))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [categoria, estado, ambito, idioma, recarga]);

  const dinero = (c: number) =>
    formatearImporteDesdeCents(c, idioma, { moneda: resumen?.moneda ?? 'EUR' });

  /*
    QUITAR PREGUNTA ANTES. La fila deja de contar en el mes, en lo que se
    factura y en lo que se paga: no es un cambio que se pueda descubrir por
    accidente. Lo borrado queda entero en la auditoria del servidor.
  */
  async function quitarGasto(g: GastoInterno) {
    if (!confirm(t(idioma, 'carga.confirmarGasto', { concepto: g.concepto }))) return;
    try {
      await api.finEliminarGasto(g.id);
      setRecarga((n) => n + 1);
    } catch {
      setError(t(idioma, 'carga.noSeQuito'));
    }
  }

  async function quitarHoras(h: HoraInterna) {
    if (!confirm(t(idioma, 'carga.confirmarHoras', { horas: horasTexto(h.minutos) }))) return;
    try {
      await api.finEliminarHoras(h.id);
      setRecarga((n) => n + 1);
    } catch {
      setError(t(idioma, 'carga.noSeQuito'));
    }
  }

  /* «75,5 h» y no «75.5 h»: el separador decimal es el del idioma. */
  const horasTexto = (minutos: number) =>
    `${(minutos / 60).toLocaleString(idioma, { maximumFractionDigits: 1 })} h`;

  const categorias = useMemo(
    () => [...new Set((gastos ?? []).map((g) => g.categoria))].sort(),
    [gastos],
  );

  /* Las personas y los meses salen de LOS DOS conjuntos: si no, elegir a alguien
     en horas y cambiar de pestaña dejaría el filtro señalando a nadie. */
  const personas = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const h of horas ?? []) mapa.set(h.usuarioId, h.usuarioNombre);
    for (const g of gastos ?? []) if (g.autorId) mapa.set(g.autorId, g.autorNombre ?? '—');
    return [...mapa].sort((a, b) => a[1].localeCompare(b[1]));
  }, [horas, gastos]);

  const mesesDisponibles = useMemo(() => {
    const vistos = new Set<string>();
    for (const h of horas ?? []) vistos.add(h.fecha.slice(0, 7));
    for (const g of gastos ?? []) vistos.add(g.fecha.slice(0, 7));
    return [...vistos].sort().reverse();
  }, [horas, gastos]);

  const nombreMes = (mes: string) => {
    const [anio, m] = mes.split('-');
    const fecha = new Date(Date.UTC(Number(anio), Number(m) - 1, 1));
    return new Intl.DateTimeFormat(idioma, { month: 'short', year: 'numeric', timeZone: 'UTC' })
      .format(fecha)
      .replace('.', '');
  };

  const alternarMes = (mes: string) =>
    setMeses((previos) =>
      previos.includes(mes) ? previos.filter((m) => m !== mes) : [...previos, mes].sort(),
    );

  const consulta = normaliza(busqueda);
  const enMes = (fecha: string) => meses.length === 0 || meses.includes(fecha.slice(0, 7));
  const contiene = (campos: (string | null)[]) =>
    consulta === '' || campos.some((c) => c && normaliza(c).includes(consulta));

  const minCents = importeAcentimos(importeMin);
  const maxCents = importeAcentimos(importeMax);

  const gastosVistos = useMemo(
    () =>
      (gastos ?? []).filter(
        (g) =>
          (!persona || g.autorId === persona) &&
          enMes(g.fecha) &&
          contiene([g.concepto, g.beneficiario, g.autorNombre, t(idioma, `categoriaGasto.${g.categoria}`)]) &&
          (minCents === null || g.costeCents >= minCents) &&
          (maxCents === null || g.costeCents <= maxCents),
      ),
    [gastos, persona, meses, consulta, minCents, maxCents, idioma],
  );

  const horasVistas = useMemo(
    () =>
      (horas ?? []).filter(
        (h) =>
          (!persona || h.usuarioId === persona) &&
          enMes(h.fecha) &&
          contiene([h.descripcion, h.usuarioNombre, t(idioma, `tipoHora.${h.tipo}`)]),
      ),
    [horas, persona, meses, consulta, idioma],
  );

  const minutosVistos = horasVistas.reduce((suma, h) => suma + h.minutos, 0);
  const costeVisto = gastosVistos.reduce(
    (suma, g) => suma + (g.estado === 'RECHAZADO' ? 0 : g.costeCents),
    0,
  );

  const hayFiltro =
    persona !== '' || meses.length > 0 || busqueda.trim() !== '' || importeMin !== '' || importeMax !== '';

  const limpiar = () => {
    setPersona('');
    setMeses([]);
    setBusqueda('');
    setImporteMin('');
    setImporteMax('');
  };

  /** El nombre de la persona elegida, o el equipo entero. */
  const quien = persona ? (personas.find(([id]) => id === persona)?.[1] ?? '') : '';

  const descargarHoras = () => {
    const contexto = [
      quien || t(idioma, 'interno.todoElEquipo'),
      meses.length > 0 ? meses.map(nombreMes).join(', ') : t(idioma, 'interno.todoElHistorico'),
      busqueda.trim() ? `«${busqueda.trim()}»` : null,
      t(idioma, 'interno.registros', { total: horasVistas.length }),
    ]
      .filter(Boolean)
      .join(' · ');

    const filas: Celda[][] = horasVistas.map((h) => [
      h.fecha,
      h.usuarioNombre,
      t(idioma, `tipoHora.${h.tipo}`),
      h.descripcion ?? '',
      Math.round((h.minutos / 60) * 100) / 100,
    ]);

    const libro = construirLibro([
      {
        nombre: t(idioma, 'interno.horas'),
        titulo: `SOLUXE · ${t(idioma, 'interno.horasTitulo')}`,
        subtitulo: contexto,
        cabeceras: [
          t(idioma, 'interno.fecha'),
          t(idioma, 'interno.persona'),
          t(idioma, 'interno.tipo'),
          t(idioma, 'interno.descripcion'),
          t(idioma, 'interno.horasCol'),
        ],
        anchos: [12, 26, 18, 56, 10],
        formatos: ['fecha', 'texto', 'texto', 'texto', 'horas'],
        filas,
        total: ['', '', '', t(idioma, 'interno.total'), Math.round((minutosVistos / 60) * 100) / 100],
      },
    ]);

    const trozo = (texto: string) =>
      normaliza(texto).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const nombre = [
      'SOLUXE-horas',
      // Un fichero llamado «equipo» con las horas de una sola persona miente
      // en cuanto sale del navegador y alguien lo abre por su nombre.
      persona ? trozo(quien) : esDireccion ? 'equipo' : 'mis-horas',
      meses.length > 0 ? meses.join('_') : 'historico',
    ].join('-');
    descargar(libro, `${nombre}.xlsx`);
  };

  const filtrosComunes = (
    <div className="filtros-interno">
      <select
        aria-label={t(idioma, 'interno.ambito')}
        value={ambito}
        onChange={(e) => setAmbito(e.target.value as typeof ambito)}
      >
        <option value="INTERNO">{t(idioma, 'interno.ambitoInterno')}</option>
        <option value="PROPIEDADES">{t(idioma, 'interno.ambitoCasas')}</option>
        <option value="TODO">{t(idioma, 'interno.ambitoTodo')}</option>
      </select>
      {esDireccion && (
        <select
          aria-label={t(idioma, 'interno.persona')}
          value={persona}
          onChange={(e) => setPersona(e.target.value)}
        >
          <option value="">{t(idioma, 'interno.todoElEquipo')}</option>
          {personas.map(([id, nombre]) => (
            <option key={id} value={id}>
              {nombre}
            </option>
          ))}
        </select>
      )}

      <input
        type="search"
        className="filtros-busqueda"
        placeholder={t(idioma, 'interno.buscar')}
        aria-label={t(idioma, 'interno.buscar')}
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
      />

      {hayFiltro && (
        <button type="button" className="secundario" onClick={limpiar}>
          {t(idioma, 'interno.limpiar')}
        </button>
      )}
    </div>
  );

  const chipsMeses = mesesDisponibles.length > 0 && (
    <div className="chips-mes" role="group" aria-label={t(idioma, 'interno.meses')}>
      {mesesDisponibles.map((mes) => (
        <button
          key={mes}
          type="button"
          className="chip-mes"
          aria-pressed={meses.includes(mes)}
          onClick={() => alternarMes(mes)}
        >
          {nombreMes(mes)}
        </button>
      ))}
    </div>
  );

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'interno.titulo')}</h1>
          <p>
            {t(idioma, esDireccion ? 'interno.subtitulo' : 'interno.subtituloPropio')}
          </p>
        </div>
        <button type="button" className="secundario" onClick={onVolver}>
          {t(idioma, 'app.volver')}
        </button>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {resumen && (
        <div className="rejilla kpis">
          <div className="tarjeta kpi-card">
            <h3>{t(idioma, 'interno.total')}</h3>
            <span className="kpi-valor">{dinero(resumen.totalCents)}</span>
            <span className="kpi-pie">
              {t(idioma, 'interno.registros', { total: resumen.totalGastos })}
            </span>
          </div>
          <div className="tarjeta kpi-card">
            <h3>{t(idioma, 'interno.pendientes')}</h3>
            <span className="kpi-valor">{resumen.pendientes}</span>
            <span className={`kpi-pie ${resumen.pendientes > 0 ? 'ambar' : ''}`}>
              {t(idioma, 'interno.pendientesPie')}
            </span>
          </div>
          <div className="tarjeta kpi-card">
            <h3>{t(idioma, 'interno.sinContabilizar')}</h3>
            <span className="kpi-valor">{resumen.sinContabilizar}</span>
            <span className="kpi-pie">{t(idioma, 'interno.sinContabilizarPie')}</span>
          </div>
          <div className="tarjeta kpi-card">
            <h3>{t(idioma, 'interno.horas')}</h3>
            <span className="kpi-valor">{horasTexto(resumen.totalMinutos)}</span>
            <span className="kpi-pie">
              {t(idioma, 'interno.registros', { total: resumen.totalRegistrosHoras })}
            </span>
          </div>
        </div>
      )}

      <div className="conmutador-vista" role="group" aria-label={t(idioma, 'interno.titulo')}>
        <button
          type="button"
          aria-pressed={pestana === 'gastos'}
          onClick={() => setPestana('gastos')}
        >
          {t(idioma, 'interno.gastos')}
        </button>
        <button type="button" aria-pressed={pestana === 'horas'} onClick={() => setPestana('horas')}>
          {t(idioma, 'interno.horas')}
        </button>
      </div>

      {pestana === 'gastos' && (
        <>
          <div className="filtros-interno">
            <select
              aria-label={t(idioma, 'interno.categoria')}
              value={categoria}
              onChange={(e) => setCategoria(e.target.value)}
            >
              <option value="">{t(idioma, 'interno.todasCategorias')}</option>
              {categorias.map((c) => (
                <option key={c} value={c}>
                  {t(idioma, `categoriaGasto.${c}`)}
                </option>
              ))}
            </select>
            <select
              aria-label={t(idioma, 'interno.estado')}
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              <option value="">{t(idioma, 'interno.todosEstados')}</option>
              {['PENDIENTE_APROBACION', 'APROBADO', 'RECHAZADO', 'PAGADO'].map((s) => (
                <option key={s} value={s}>
                  {t(idioma, `estadoGasto.${s}`)}
                </option>
              ))}
            </select>
            {/* El importe se escribe en euros; por dentro todo son céntimos. */}
            <input
              type="text"
              inputMode="decimal"
              className="filtros-importe"
              placeholder={t(idioma, 'interno.importeDesde')}
              aria-label={t(idioma, 'interno.importeDesde')}
              value={importeMin}
              onChange={(e) => setImporteMin(e.target.value)}
            />
            <input
              type="text"
              inputMode="decimal"
              className="filtros-importe"
              placeholder={t(idioma, 'interno.importeHasta')}
              aria-label={t(idioma, 'interno.importeHasta')}
              value={importeMax}
              onChange={(e) => setImporteMax(e.target.value)}
            />
          </div>
          {filtrosComunes}
          {chipsMeses}

          {gastos === null && !error && <p>{t(idioma, 'app.cargando')}</p>}
          {gastos !== null && (
            <p className="suave recuento">
              {t(idioma, 'interno.registros', { total: gastosVistos.length })} ·{' '}
              <span className="numerico">{dinero(costeVisto)}</span>
            </p>
          )}
          {gastos !== null && gastosVistos.length === 0 && (
            <div className="tarjeta">
              <p>{t(idioma, 'interno.vacio')}</p>
            </div>
          )}
          {gastos !== null && gastosVistos.length > 0 && (
            <div className="tarjeta tabla-scroll">
              <table className="tabla-interno">
                <thead>
                  <tr>
                    <th scope="col" className="col-fecha">
                      {t(idioma, 'interno.fecha')}
                    </th>
                    <th scope="col">{t(idioma, 'interno.concepto')}</th>
                    <th scope="col" className="col-persona">
                      {t(idioma, 'interno.beneficiario')}
                    </th>
                    <th scope="col" className="col-persona">
                      {t(idioma, 'interno.persona')}
                    </th>
                    {/* La casa solo cuando puede haberla: una columna de guiones no dice nada. */}
                    {ambito !== 'INTERNO' && (
                      <th scope="col">{t(idioma, 'interno.propiedad')}</th>
                    )}
                    <th scope="col">{t(idioma, 'interno.categoria')}</th>
                    <th scope="col">{t(idioma, 'interno.estado')}</th>
                    <th scope="col">{t(idioma, 'interno.contab')}</th>
                    <th scope="col" className="col-factura">
                      {t(idioma, 'gasto.factura')}
                    </th>
                    <th scope="col" className="num">
                      {t(idioma, 'interno.importe')}
                    </th>
                    {esDireccion && (
                      <th scope="col" className="col-acciones">
                        <span className="solo-lectores">{t(idioma, 'carga.acciones')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {gastosVistos.map((g) => (
                    <tr
                      key={g.id}
                      className="fila-pulsable"
                      tabIndex={0}
                      role="button"
                      aria-label={t(idioma, 'gasto.verFactura')}
                      onClick={() => setFactura(g)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setFactura(g);
                        }
                      }}
                    >
                      <td className="numerico col-fecha">{fechaLarga(g.fecha, idioma)}</td>
                      <td>
                        {g.concepto}
                        {g.recurrente && <span className="chip-area"> ↻</span>}
                        {g.anticipo && <span className="chip-sla"> anticipo</span>}
                      </td>
                      <td className="col-persona">
                        {g.beneficiario ?? <span className="suave">—</span>}
                      </td>
                      <td className="col-persona">
                        {g.autorNombre ?? <span className="suave">—</span>}
                      </td>
                      {ambito !== 'INTERNO' && (
                        <td>
                          {g.propiedadNombre ?? (
                            <span className="suave">{t(idioma, 'carga.sinPropiedad')}</span>
                          )}
                        </td>
                      )}
                      <td>{t(idioma, `categoriaGasto.${g.categoria}`)}</td>
                      <td>
                        <span className={`pastilla ${CLASE_ESTADO[g.estado] ?? 'neutra'}`}>
                          {t(idioma, `estadoGasto.${g.estado}`)}
                        </span>
                      </td>
                      <td>
                        {g.contabilizacion === 'CONTABILIZADO' ? (
                          <span className="pastilla verde">{t(idioma, 'interno.contabSi')}</span>
                        ) : (
                          <span className="suave">{t(idioma, 'interno.contabNo')}</span>
                        )}
                      </td>
                      <td className="col-factura">
                        <SenalFactura idioma={idioma} adjuntos={g.adjuntos} />
                      </td>
                      <td className="num numerico">{dinero(g.costeCents)}</td>
                      {esDireccion && (
                        <td className="col-acciones">
                          <AccionesCarga
                            idioma={idioma}
                            onEditar={() => setEditandoGasto(g)}
                            onEliminar={() => void quitarGasto(g)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {pestana === 'horas' && (
        <>
          {filtrosComunes}
          {chipsMeses}

          {horas === null && <p>{t(idioma, 'app.cargando')}</p>}
          {horas !== null && (
            <div className="recuento-fila">
              <p className="suave recuento">
                {t(idioma, 'interno.registros', { total: horasVistas.length })} ·{' '}
                <span className="numerico">{horasTexto(minutosVistos)}</span>
              </p>
              <button
                type="button"
                className="secundario"
                disabled={horasVistas.length === 0}
                onClick={descargarHoras}
              >
                <Icono nombre="descargar" /> {t(idioma, 'interno.descargarExcel')}
              </button>
            </div>
          )}
          {horas !== null && horasVistas.length === 0 && (
            <div className="tarjeta">
              <p>{t(idioma, 'interno.vacio')}</p>
            </div>
          )}
          {horas !== null && horasVistas.length > 0 && (
            <div className="tarjeta tabla-scroll">
              <table className="tabla-interno">
                <thead>
                  <tr>
                    <th scope="col" className="col-fecha">
                      {t(idioma, 'interno.fecha')}
                    </th>
                    <th scope="col" className="col-persona">
                      {t(idioma, 'interno.persona')}
                    </th>
                    {ambito !== 'INTERNO' && (
                      <th scope="col">{t(idioma, 'interno.propiedad')}</th>
                    )}
                    <th scope="col">{t(idioma, 'interno.tipo')}</th>
                    <th scope="col">{t(idioma, 'interno.descripcion')}</th>
                    <th scope="col" className="num">
                      {t(idioma, 'interno.horasCol')}
                    </th>
                    {esDireccion && (
                      <th scope="col" className="col-acciones">
                        <span className="solo-lectores">{t(idioma, 'carga.acciones')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {horasVistas.map((h) => (
                    <tr key={h.id}>
                      <td className="numerico col-fecha">{fechaLarga(h.fecha, idioma)}</td>
                      <td className="col-persona">{h.usuarioNombre}</td>
                      {ambito !== 'INTERNO' && (
                        <td>
                          {h.propiedadNombre ?? (
                            <span className="suave">{t(idioma, 'carga.sinPropiedad')}</span>
                          )}
                        </td>
                      )}
                      <td>{t(idioma, `tipoHora.${h.tipo}`)}</td>
                      <td>{h.descripcion ?? <span className="suave">—</span>}</td>
                      <td className="num numerico">{horasTexto(h.minutos)}</td>
                      {esDireccion && (
                        <td className="col-acciones">
                          <AccionesCarga
                            idioma={idioma}
                            onEditar={() => setEditandoHoras(h)}
                            onEliminar={() => void quitarHoras(h)}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editandoGasto && (
        <ModalEditarGasto
          idioma={idioma}
          gasto={editandoGasto}
          onGuardado={() => setRecarga((n) => n + 1)}
          onCerrar={() => setEditandoGasto(null)}
        />
      )}

      {editandoHoras && (
        <ModalEditarHoras
          idioma={idioma}
          hora={editandoHoras}
          onGuardado={() => setRecarga((n) => n + 1)}
          onCerrar={() => setEditandoHoras(null)}
        />
      )}

      {factura && (
        <FacturaDelGasto
          idioma={idioma}
          gastoId={factura.id}
          concepto={factura.concepto}
          adjuntos={factura.adjuntos}
          onSubida={(adjunto) => {
            // Se parchea la fila en vez de volver a pedir la lista entera: es
            // lo mismo que acaba de pasar, y ya se sabe.
            setGastos((antes) =>
              (antes ?? []).map((g) =>
                g.id === factura.id ? { ...g, adjuntos: [...g.adjuntos, adjunto] } : g,
              ),
            );
          }}
          onCerrar={() => setFactura(null)}
        />
      )}
    </main>
  );
}

/** Sin tildes y en minúsculas: buscar «jardin» tiene que encontrar «jardín». */
function normaliza(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** Lo que se escribe en el filtro de importe, en céntimos. Vacío es «sin tope». */
function importeAcentimos(texto: string): number | null {
  const limpio = texto.replace(/\s/g, '').replace(',', '.');
  if (limpio === '') return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
